import type {GroupField, ImageField, KeyTextField, LinkField} from '@prismicio/client'
import {isFilled} from '@prismicio/client'
import {PrismicNextImage} from '@prismicio/next/pages'
import {PrismicRichText, SliceZone} from '@prismicio/react'
import dayjs from 'dayjs'
import type {
  ArticleDocument,
  ArticleDocumentData,
  AuthorDocumentData,
  ButtonDocument,
  CustomerStoryDocument,
  CustomerStoryDocumentData,
  HomePageDocumentData,
  NavigationDocumentData,
  PageDocumentData,
} from 'prismicio-types'
import {memo} from 'react'
import {components} from 'slices'
import {Layout} from 'src/common/Layout'
import {Meta} from 'src/common/Meta'
import {PageFaqSEO} from 'src/common/PageFaqSEO'
import type {ParentUid} from 'src/common/utils/documentHelpers'

export type CustomerStorySliceZoneContext = {
  category: CustomerStoryDocumentData['category']
  industry: CustomerStoryDocumentData['industry']
  location: string
  headcount: string
  readingTime: KeyTextField
  companyLogo?: ImageField
  mainImage?: ImageField
  quotes?: GroupField
  youtubeVideoSource?: LinkField
}

export const CustomerStory = memo(function (props: {
  navigationData: NavigationDocumentData
  page: CustomerStoryDocument
  parentUids?: Array<ParentUid>
  breadcrumbs?:
    | PageDocumentData['breadcrumbs']
    | ArticleDocumentData['breadcrumbs']
    | CustomerStoryDocumentData['breadcrumbs']
    | HomePageDocumentData['breadcrumbs']
  moreCustomerStories?: CustomerStoryDocument[]
  buttonsData?: Array<ButtonDocument>
  articles?: Array<ArticleDocument>
}) {
  return (
    <Layout
      alternateLanguages={
        props.parentUids
          ? props.page.alternate_languages.map((altLang) => ({
              ...altLang,
              parentUid: props.parentUids?.find((parentUid) => parentUid.lang === altLang.lang)?.uid,
            }))
          : props.page.alternate_languages
      }
      navigationData={props.navigationData}
      pageUid={props.page.uid}
      locale={props.page.lang}
      breadcrumbs={props.breadcrumbs}
      parentUids={props.parentUids}
    >
      <Meta
        {...(isFilled.keyText(props.page.data.meta_description) && {description: props.page.data.meta_description})}
        {...(isFilled.keyText(props.page.data.meta_title) && {title: props.page.data.meta_title})}
        {...(isFilled.image(props.page.data.meta_image) && {imageUrl: props.page.data.meta_image.url})}
      />

      {props.page.data.enable_v2 ? null : (
        <section className="grid grid-cols-main gap-y-6 pb-12 pt-4 sm:pb-16 sm:pt-6 md:pb-20 md:pt-18 lg:py-24">
          <div className="col-[inner] grid max-w-md gap-y-6 self-center md:col-end-[track-end_6] md:justify-self-end">
            <div className="flex gap-x-2">
              <div className="flex h-[22px] items-center rounded-4 bg-5a7269 px-2 text-12 font-medium leading-4 text-ffffff">
                Customer stories
              </div>

              <div className="flex h-[22px] items-center rounded-4 bg-ecf0ef px-2 text-12 font-medium leading-4 text-5a7269">
                {props.page.data.tag}
              </div>
            </div>

            <PrismicRichText
              components={{
                heading1: ({children}) => (
                  <h1 className="text-32 leading-10 -tracking-common text-2a2928 lg:text-36 lg:leading-11">
                    {children}
                  </h1>
                ),
              }}
              field={props.page.data.title}
            />

            <div className="items-center text-14 leading-5 text-666563 sm:flex">
              {isFilled.contentRelationship(props.page.data.author) && (
                <PrismicNextImage
                  className="size-6"
                  field={(props.page.data.author.data as AuthorDocumentData).photo}
                  height={24}
                  width={24}
                />
              )}

              {isFilled.contentRelationship(props.page.data.author) && (
                <div className="sm:ml-2 sm:mr-3 sm:border-r sm:border-r-b8b6b4 sm:pr-3">
                  {(props.page.data.author.data as AuthorDocumentData).name}
                </div>
              )}

              <div className="flex items-center gap-x-1">
                <span>
                  {dayjs(
                    isFilled.date(props.page.data.date)
                      ? props.page.data.date
                      : props.page.last_publication_date || props.page.first_publication_date,
                  ).format('MMMM DD, YYYY')}
                </span>

                <div className="size-1 rounded-full bg-666563" />
                {isFilled.keyText(props.page.data.reading_time) && <span>{props.page.data.reading_time}</span>}
              </div>
            </div>
          </div>

          <PrismicNextImage
            className="col-[inner] w-full max-w-[453px] md:col-start-[track-start_7] md:justify-self-end"
            field={props.page.data.image}
            quality={90}
            priority
            sizes="(max-width: 640px) calc(100vw - 48px), (max-width: 768px) calc(100vw - 80px), (max-width: 1440px) 45vw, 453px"
          />
        </section>
      )}

      <PageFaqSEO slices={props.page.data.slices} />

      <SliceZone
        components={components}
        slices={props.page.data.slices}
        context={{
          category: props.page.data.category,
          industry: props.page.data.industry,
          location: props.page.data.location,
          headcount: props.page.data.employees_number,
          moreCustomerStories: props.moreCustomerStories,
          buttonsData: props.buttonsData,
          readingTime: props.page.data.reading_time,
          companyLogo: isFilled.image(props.page.data.company_logo) ? props.page.data.company_logo : undefined,
          mainImage: isFilled.image(props.page.data.image) ? props.page.data.image : undefined,
          quotes: props.page.data.quotes?.length > 0 ? props.page.data.quotes : undefined,
          youtubeVideoSource: isFilled.link(props.page.data.youtube_video_source)
            ? props.page.data.youtube_video_source
            : undefined,
          articles: props.articles ?? [],
        }}
      />
    </Layout>
  )
})
